/**
 * ComputedAttributesPage - Página de Atributos Calculados Premium
 * Migrado para padrão Premium Fleet
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Checkbox,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FunctionsIcon from '@mui/icons-material/Functions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NumbersIcon from '@mui/icons-material/Numbers';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import SelectField from '../common/components/SelectField';
import { useAdministrator } from '../common/util/permissions';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useEffectAsync, useCatch } from '../reactHelper';
import useCrudExports from '../common/hooks/useCrudExports';
import { formatBoolean } from '../common/util/formatter';
import { snackBarDurationLongMs } from '../common/util/duration';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTableSelection } from '../common/hooks';

// Componentes extraídos
import ComputedAttributesTableRow from './components/ComputedAttributesTableRow';
import {
  PremiumStatCards,
  PremiumEmptyState,
  BulkActionsBar,
  BulkDeleteDialog,
} from '../common/components/premium';

const ComputedAttributesPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const administrator = useAdministrator();

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Bulk action states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Estados para teste
  const [deviceId, setDeviceId] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [result, setResult] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Fetch de dados
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/attributes/computed');
      setItems(await response.json());
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // Estatísticas
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter((i) => !i.disabled).length,
    disabled: items.filter((i) => i.disabled).length,
    string: items.filter((i) => i.type === 'string').length,
    number: items.filter((i) => i.type === 'number').length,
  }), [items]);

  // Filtros para StatCards (usa tokens dinâmicos)
  const statFilters = useMemo(() => [
    { value: '', label: t('sharedAll'), count: stats.total, color: tokens?.accent?.primary, Icon: FunctionsIcon },
    { value: 'active', label: t('sharedActive'), count: stats.active, color: tokens?.semantic?.success, Icon: CheckCircleIcon },
    { value: 'disabled', label: t('sharedDisabled'), count: stats.disabled, color: tokens?.semantic?.error, Icon: BlockIcon },
    { value: 'string', label: t('attributeTypeString'), count: stats.string, color: tokens?.semantic?.info, Icon: TextFieldsIcon },
    { value: 'number', label: t('attributeTypeNumber'), count: stats.number, color: tokens?.semantic?.warning, Icon: NumbersIcon },
  ], [t, stats, tokens]);

  // Filtragem + Busca
  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter === 'active') {
      result = result.filter((i) => !i.disabled);
    } else if (statusFilter === 'disabled') {
      result = result.filter((i) => i.disabled);
    } else if (statusFilter === 'string') {
      result = result.filter((i) => i.type === 'string');
    } else if (statusFilter === 'number') {
      result = result.filter((i) => i.type === 'number');
    }
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.description?.toLowerCase().includes(lower) ||
          i.attribute?.toLowerCase().includes(lower) ||
          i.expression?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower)
      );
    }
    return result;
  }, [items, statusFilter, searchText]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Bulk selection hook
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isIndeterminate,
    hasSelection,
  } = useTableSelection(filteredItems);

  // Bulk delete handler
  const handleBulkDelete = useCallback(async () => {
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedIds.map((id) =>
          fetchOrThrow(`/api/attributes/computed/${id}`, { method: 'DELETE' })
        )
      );
      clearSelection();
      setDeleteDialogOpen(false);
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Erro ao excluir atributos:', error);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, clearSelection]);

  // Handlers
  const handleFilterChange = useCallback((value) => {
    setStatusFilter(value);
    setPage(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatusFilter('');
    setSearchText('');
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  // Teste do atributo
  const testAttribute = useCatch(async () => {
    if (!selectedItem || !deviceId) return;
    const query = new URLSearchParams({ deviceId });
    const url = `/api/attributes/computed/test?${query.toString()}`;
    const response = await fetchOrThrow(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(selectedItem),
    });
    const text = await response.text();
    setResult(text);
    setOpenSnackbar(true);
  });

  const handleTestAttribute = useCallback((id) => {
    const item = items.find((i) => i.id === id);
    setSelectedItem(item);
    setOpenDialog(true);
  }, [items]);

  // Exportações
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'description', headerNameKey: 'sharedDescription' },
    { field: 'attribute', headerNameKey: 'sharedAttribute' },
    { field: 'expression', headerNameKey: 'attributeExpression' },
    { field: 'type', headerNameKey: 'sharedType' },
    {
      field: 'disabled',
      headerNameKey: 'sharedDisabled',
      valueGetter: ({ row }) => formatBoolean(row.disabled, t),
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportAttributesTitle',
    headerKey: 'reportAttributesHeader',
    rows: filteredItems,
    columns: exportColumns,
    t,
    options: { includeWatermark: true },
  });

  // Check if has active filters
  const hasFilters = Boolean(statusFilter || searchText.trim());

  // Header styles for premium table
  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens?.text?.secondary,
    borderBottom: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
    py: 1.5,
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedComputedAttributes']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Loading State */}
          {loading && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography variant="body1" sx={{ color: tokens?.text?.secondary }}>
                {t('sharedLoading') || 'Carregando...'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* CARDS DE ESTATÍSTICAS */}
              <PremiumStatCards
                filters={statFilters}
                activeFilter={statusFilter}
                onFilterChange={handleFilterChange}
              />

              {/* BARRA DE AÇÕES */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: { xs: 'stretch', md: 'center' },
                  flexShrink: 0,
                }}
              >
                <TextField
                  size="small"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                  placeholder={t('sharedSearch')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: tokens?.accent?.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: tokens?.surface?.glass,
                      borderRadius: tokens?.radius?.md,
                      '&:hover': {
                        backgroundColor: tokens?.surface?.hover,
                      },
                    },
                  }}
                />
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    gap: 0.5,
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  }}
                >
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    startIcon={<AddCircleIcon />}
                    onClick={() => navigate('/settings/attribute')}
                    disabled={!administrator}
                    sx={{
                      backgroundColor: tokens?.accent?.primary,
                      '&:hover': { backgroundColor: tokens?.accent?.hover },
                    }}
                  >
                    {t('sharedAdd')}
                  </Button>
                  {administrator && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      endIcon={<ExpandMoreIcon />}
                      onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                      sx={{
                        color: tokens?.text?.primary,
                        borderColor: tokens?.border?.default,
                        borderRadius: tokens?.radius?.md,
                        fontWeight: 500,
                        textTransform: 'none',
                        transition: tokens?.transitions?.all,
                        '&:hover': {
                          borderColor: tokens?.accent?.primary,
                          bgcolor: `${tokens?.accent?.primary}10`,
                        },
                      }}
                    >
                      {t('reportExport') || 'Exportar'}
                    </Button>
                  )}
                </Stack>
              </Box>

              {/* Results count & Bulk Actions Bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredItems.length} {t('sharedComputedAttributes') || 'atributos calculados'}
                  {hasFilters && ` (${t('sharedFiltered') || 'filtrado'})`}
                </Typography>

                {hasSelection && (
                  <BulkActionsBar
                    selectedCount={selectedCount}
                    onClearSelection={clearSelection}
                    onDelete={() => setDeleteDialogOpen(true)}
                    loading={bulkActionLoading}
                    entityName={t('sharedComputedAttributes') || 'atributo(s)'}
                  />
                )}
              </Box>

              {/* TABELA */}
              <Paper
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  borderRadius: tokens?.radius?.lg || '12px',
                  backgroundColor: tokens?.surface?.glass,
                  border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <TableContainer
                  className="premium-table-container"
                  sx={{ maxHeight: 600 }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ ...headerCellSx, width: 48 }}>
                          <Checkbox
                            indeterminate={isIndeterminate}
                            checked={isAllSelected}
                            onChange={selectAll}
                            sx={{
                              color: tokens?.text?.tertiary,
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                                color: tokens?.accent?.primary,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 48 }} />
                        <TableCell sx={{ ...headerCellSx, width: 60 }}>ID</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedDescription')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedAttribute')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('attributeExpression')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedType')}</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>Status</TableCell>
                        {administrator && (
                          <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>{t('sharedActions')}</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginated.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={administrator ? 9 : 8} sx={{ border: 0, p: 0 }}>
                            <PremiumEmptyState
                              icon={FunctionsIcon}
                              title={t('attributesEmpty') || 'Nenhum atributo calculado cadastrado'}
                              titleFiltered={t('attributesNotFound') || 'Nenhum atributo encontrado'}
                              subtitle={t('attributesEmptyHint') || 'Comece adicionando seu primeiro atributo calculado'}
                              subtitleFiltered={t('attributesNotFoundHint') || 'Tente ajustar os filtros de busca'}
                              hasFilters={hasFilters}
                              onClearFilters={handleClearFilters}
                              addPath="/settings/attribute"
                              addLabel={t('attributeAdd') || 'Adicionar Atributo'}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginated.map((row) => (
                          <ComputedAttributesTableRow
                            key={row.id}
                            row={row}
                            onRefresh={handleRefresh}
                            onTest={handleTestAttribute}
                            selected={isSelected(row.id)}
                            onSelect={toggleSelect}
                            isAdmin={administrator}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredItems.length > 0 && (
                  <TablePagination
                    component="div"
                    count={filteredItems.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage={t('rowsPerPage') || 'Linhas por página'}
                    sx={{
                      borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.8rem',
                        color: tokens?.text?.secondary,
                      },
                    }}
                  />
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: tokens?.surface?.elevated,
            borderRadius: tokens?.radius?.lg,
            border: `1px solid ${tokens?.border?.subtle}`,
          },
        }}
      >
        <MenuItem
          onClick={() => { handleExportCsv(); setExportMenuAnchor(null); }}
          sx={{ color: tokens?.text?.primary }}
        >
          <FileDownloadIcon sx={{ mr: 1, fontSize: 18, color: tokens?.semantic?.success }} />
          CSV
        </MenuItem>
        <MenuItem
          onClick={() => { handleExportXlsx(); setExportMenuAnchor(null); }}
          sx={{ color: tokens?.text?.primary }}
        >
          <TableChartIcon sx={{ mr: 1, fontSize: 18, color: tokens?.semantic?.info }} />
          Excel
        </MenuItem>
        <MenuItem
          onClick={() => { handleExportPdf(); setExportMenuAnchor(null); }}
          sx={{ color: tokens?.text?.primary }}
        >
          <PictureAsPdfIcon sx={{ mr: 1, fontSize: 18, color: tokens?.semantic?.error }} />
          PDF
        </MenuItem>
      </Menu>

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        selectedCount={selectedCount}
        loading={bulkActionLoading}
        entityName={t('sharedComputedAttributes') || 'atributo(s)'}
      />

      {/* DIALOG TESTE */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: tokens?.surface?.elevated,
            borderRadius: tokens?.radius?.lg,
            border: `1px solid ${tokens?.border?.subtle}`,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: tokens?.text?.primary }}>
          <PlayArrowIcon sx={{ color: tokens?.semantic?.success }} />
          {t('attributeTest')}
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: tokens?.border?.subtle }}>
          <SelectField
            endpoint="/api/devices"
            label={t('sharedDevice')}
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            size="small"
            fullWidth
          />
        </DialogContent>

        <DialogActions sx={{ borderTop: `1px solid ${tokens?.border?.subtle}` }}>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setTimeout(() => document.activeElement.blur(), 50);
              setDeviceId('');
              setSelectedItem(null);
            }}
            sx={{ color: tokens?.text?.secondary }}
          >
            {t('sharedCancel')}
          </Button>

          <Button
            onClick={async () => {
              await testAttribute();
              setOpenDialog(false);
              setTimeout(() => document.activeElement.blur(), 50);
              setDeviceId('');
              setSelectedItem(null);
            }}
            variant="contained"
            disabled={!deviceId}
            sx={{
              backgroundColor: tokens?.semantic?.success,
              '&:hover': { backgroundColor: `${tokens?.semantic?.success}dd` },
            }}
          >
            {t('sharedTest')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR RESULTADO */}
      <Snackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        autoHideDuration={snackBarDurationLongMs}
        message={result}
      />
    </PageLayout>
  );
};

export default ComputedAttributesPage;
